<template>
  <div class="sx">
    <header class="sx-head">
      <span class="sx-eyebrow">Paramètres</span>
      <h1 class="sx-title">Types client</h1>
      <p class="sx-sub">Catégories attribuées aux clients (société, auto-entrepreneur, particulier…) dans leur fiche.</p>
    </header>

    <SettingsNav />

    <div class="sx-grid">
      <!-- ── Add ─────────────────────────────────────────────────── -->
      <form class="sx-card sx-form-card" novalidate @submit.prevent="submitForm">
        <header class="sx-card-head">
          <span class="sx-card-icon"><i class="ph ph-users-three" aria-hidden="true"></i></span>
          <div>
            <h2>Nouveau type client</h2>
            <p>Il sera proposé dans la fiche client.</p>
          </div>
        </header>

        <div class="sx-field">
          <label for="tc-new-name">Nom du type <span class="sx-req" aria-hidden="true">*</span></label>
          <input
            id="tc-new-name"
            v-model="type.name"
            type="text"
            class="form-control"
            :class="{ 'is-invalid': errors.name }"
            maxlength="100"
            placeholder="Ex. Société"
            @input="delete errors.name"
          />
          <span v-if="errors.name" class="sx-err">{{ errors.name }}</span>
        </div>

        <button type="submit" class="btn btn-primary sx-submit" :disabled="saving">
          <span v-if="saving" class="spinner-border spinner-border-sm" role="status"></span>
          Ajouter le type
        </button>
      </form>

      <!-- ── List ────────────────────────────────────────────────── -->
      <section class="sx-card" aria-labelledby="tc-list-title">
        <div class="sx-list-head">
          <h2 id="tc-list-title">Types client <span class="sx-count">{{ types.length }}</span></h2>
          <div class="sx-search">
            <i class="ph ph-magnifying-glass" aria-hidden="true"></i>
            <input v-model="search" type="search" placeholder="Rechercher un type…" aria-label="Rechercher un type client" />
          </div>
        </div>

        <div v-if="loading">
          <span v-for="n in 4" :key="n" class="sx-skel"></span>
        </div>
        <div v-else-if="loadError" class="sx-load-error" role="alert">
          <i class="ph ph-warning-circle" aria-hidden="true"></i>
          Impossible de charger les types client.
          <button type="button" class="btn btn-sm btn-light" @click="fetchTypes">Réessayer</button>
        </div>
        <div v-else-if="!filtered.length" class="sx-empty">
          <i class="ph ph-users-three" aria-hidden="true"></i>
          <p>{{ search ? "Aucun type ne correspond à votre recherche." : "Aucun type client pour le moment." }}</p>
        </div>
        <ul v-else class="sx-rows">
          <li v-for="item in filtered" :key="item.id" class="sx-row" :class="{ 'is-new': item.id === lastAddedId }">
            <span class="sx-avatar" aria-hidden="true">{{ initials(item.name) }}</span>
            <div class="sx-main">
              <strong>{{ item.name }}</strong>
              <small>{{ num(item.NbClient) > 0 ? "Utilisé" : "Non utilisé" }}</small>
            </div>
            <div class="sx-meta">
              <span class="sx-tag" :class="{ 'tone-accent': num(item.NbClient) > 0 }">
                <i class="ph ph-storefront" aria-hidden="true"></i><b>{{ num(item.NbClient) }}</b> client(s)
              </span>
            </div>
            <div class="sx-actions">
              <button type="button" class="sx-icon-btn" :aria-label="`Modifier ${item.name}`" title="Modifier" @click="openEditModal(item.id)">
                <i class="ph ph-pencil-simple" aria-hidden="true"></i>
              </button>
              <button
                type="button"
                class="sx-icon-btn is-danger"
                :aria-label="`Supprimer ${item.name}`"
                :title="num(item.NbClient) > 0 ? 'Impossible : type attribué à des clients' : 'Supprimer'"
                :disabled="num(item.NbClient) > 0"
                @click="deleteType(item.id)"
              >
                <i class="ph ph-trash" aria-hidden="true"></i>
              </button>
            </div>
          </li>
        </ul>
      </section>
    </div>

    <!-- ── Edit modal ────────────────────────────────────────────── -->
    <div id="editTypeClientModal" ref="editModal" class="modal fade" tabindex="-1" aria-labelledby="editTypeClientModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <form class="modal-content" novalidate @submit.prevent="updateType">
          <div class="modal-header">
            <h2 id="editTypeClientModalLabel" class="modal-title">Modifier le type client</h2>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Fermer"></button>
          </div>
          <div class="modal-body">
            <div class="sx-field">
              <label for="tc-edit-name">Nom du type <span class="sx-req" aria-hidden="true">*</span></label>
              <input id="tc-edit-name" v-model="editType.name" type="text" class="form-control" :class="{ 'is-invalid': editErrors.name }" maxlength="100" />
              <span v-if="editErrors.name" class="sx-err">{{ editErrors.name }}</span>
              <span v-else-if="editUsage > 0" class="sx-help">Le nouveau nom s'appliquera aux {{ editUsage }} client(s) de ce type.</span>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-light" data-bs-dismiss="modal">Annuler</button>
            <button type="submit" class="btn btn-primary" :disabled="savingEdit">
              <span v-if="savingEdit" class="spinner-border spinner-border-sm" role="status"></span>
              Enregistrer
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import SettingsNav from "./SettingsNav.vue";
import { computed, defineComponent, onBeforeUnmount, onMounted, reactive, ref } from "vue";
import api from "@/utils/axios";
import { useAppToast } from "@/composables/useAppToast";
import Swal from "sweetalert2";
import { Modal } from "bootstrap";

interface ClientType {
  id: number;
  name: string;
  NbClient?: number;
}

const norm = (s: string) =>
  String(s || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

export default defineComponent({
  name: "TypeClientSettings",
  components: { SettingsNav },
  setup() {
    const { showToast } = useAppToast();
    const types = ref<ClientType[]>([]);
    const loading = ref(true);
    const loadError = ref(false);
    const search = ref("");
    const lastAddedId = ref<number | null>(null);

    const num = (v: unknown) => Number(v) || 0;
    const initials = (name: string) =>
      (name || "?")
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((p) => p[0] || "")
        .join("")
        .toUpperCase();

    async function fetchTypes() {
      loading.value = true;
      loadError.value = false;
      try {
        const response = await api.get("/api/auth/typeClient_list");
        types.value = response.data || [];
      } catch (error) {
        console.error("Error fetching client types:", error);
        loadError.value = true;
      } finally {
        loading.value = false;
      }
    }
    onMounted(fetchTypes);

    const filtered = computed(() => {
      const q = norm(search.value.trim());
      return q ? types.value.filter((t) => norm(t.name).includes(q)) : types.value;
    });

    // ── Create ──
    const type = reactive({ name: "" });
    const errors = reactive<Record<string, string>>({});
    const saving = ref(false);

    async function submitForm() {
      Object.keys(errors).forEach((k) => delete errors[k]);
      const name = type.name.trim();
      if (!name) errors.name = "Le nom du type est obligatoire.";
      else if (types.value.some((t) => norm(t.name) === norm(name))) errors.name = "Ce type client existe déjà.";
      if (Object.keys(errors).length || saving.value) return;

      saving.value = true;
      try {
        const formData = new FormData();
        formData.append("name", name);
        const response = await api.post("/api/auth/typeClient_add", formData);
        if (response.data?.success || response.data?.message === "success") {
          lastAddedId.value = response.data?.data?.id ?? null;
          await fetchTypes();
          type.name = "";
          showToast("Type client ajouté.", "success");
        } else {
          showToast(response.data?.message || "L'ajout a échoué.", "error");
        }
      } catch (error: any) {
        const errs = error?.response?.data?.errors || {};
        Object.keys(errs).forEach((k) => (errors[k] = errs[k][0]));
        showToast(error?.response?.data?.message || "Une erreur est survenue lors de l'ajout.", "error");
      } finally {
        saving.value = false;
      }
    }

    // ── Delete (only unused types: the server refuses the others) ──
    function deleteType(id: number) {
      const target = types.value.find((t) => t.id === id);
      Swal.fire({
        title: "Supprimer ce type client ?",
        text: target ? `« ${target.name} » sera définitivement supprimé.` : "",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#E44A4A",
        confirmButtonText: "Oui, supprimer",
        cancelButtonText: "Annuler",
        reverseButtons: true,
      }).then(async (result: any) => {
        if (!result.isConfirmed) return;
        try {
          const res = await api.post(`/api/auth/typeClient_remove/${id}`);
          if (res.data?.success) {
            showToast("Type client supprimé.", "success");
            fetchTypes();
          } else {
            showToast(res.data?.message ?? "Vous ne pouvez pas supprimer ce type.", "error");
          }
        } catch (error: any) {
          showToast(error?.response?.data?.message || "Erreur lors de la suppression du type.", "error");
        }
      });
    }

    // ── Edit ──
    const editType = ref<{ id: number | null; name: string }>({ id: null, name: "" });
    const editUsage = ref(0);
    const editErrors = reactive<Record<string, string>>({});
    const editModal = ref<HTMLElement | null>(null);
    const savingEdit = ref(false);
    let modalInstance: Modal | null = null;

    const openEditModal = (id: number) => {
      const found = types.value.find((t) => t.id === id);
      if (!found) return;
      editType.value = { id: found.id, name: found.name };
      editUsage.value = num(found.NbClient);
      Object.keys(editErrors).forEach((k) => delete editErrors[k]);
      if (!modalInstance && editModal.value) modalInstance = new Modal(editModal.value);
      modalInstance?.show();
    };

    const updateType = async () => {
      Object.keys(editErrors).forEach((k) => delete editErrors[k]);
      const name = editType.value.name.trim();
      if (!name) editErrors.name = "Le nom du type est obligatoire.";
      else if (types.value.some((t) => t.id !== editType.value.id && norm(t.name) === norm(name)))
        editErrors.name = "Ce type client existe déjà.";
      if (Object.keys(editErrors).length || savingEdit.value) return;

      savingEdit.value = true;
      try {
        const res = await api.post(`/api/auth/typeClient_edit/${editType.value.id}`, { name });
        if (res.data?.success || res.data?.message === "success") {
          modalInstance?.hide();
          await fetchTypes();
          showToast("Type client modifié.", "success");
        } else {
          showToast(res.data?.message ?? "Erreur lors de la mise à jour.", "error");
        }
      } catch (error: any) {
        const errs = error?.response?.data?.errors || {};
        Object.keys(errs).forEach((k) => (editErrors[k] = errs[k][0]));
        showToast(error?.response?.data?.message || "Erreur lors de la mise à jour.", "error");
      } finally {
        savingEdit.value = false;
      }
    };
    onBeforeUnmount(() => modalInstance?.dispose());

    return {
      types,
      loading,
      loadError,
      search,
      filtered,
      lastAddedId,
      num,
      initials,
      fetchTypes,
      type,
      errors,
      saving,
      submitForm,
      deleteType,
      editType,
      editUsage,
      editErrors,
      editModal,
      savingEdit,
      openEditModal,
      updateType,
    };
  },
});
</script>
